<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['rate']);

const messageContainer = ref(null);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isUserMessage = sender => sender === 'user';

const getMessageAlignment = sender =>
  isUserMessage(sender) ? 'justify-end' : 'justify-start';

const getMessageDirection = sender =>
  isUserMessage(sender) ? 'flex-row-reverse' : 'flex-row';

const getAvatarName = sender =>
  isUserMessage(sender)
    ? t('CAPTAIN.PLAYGROUND.USER')
    : t('CAPTAIN.PLAYGROUND.ASSISTANT');

const getMessageStyle = sender =>
  isUserMessage(sender)
    ? 'bg-n-solid-blue text-n-slate-12 rounded-br-sm rounded-bl-xl rounded-t-xl'
    : 'bg-n-solid-iris text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl';

const ratingLabel = rating =>
  ({
    good: t('CAPTAIN.PLAYGROUND.RATINGS.GOOD'),
    acceptable: t('CAPTAIN.PLAYGROUND.RATINGS.ACCEPTABLE'),
    poor: t('CAPTAIN.PLAYGROUND.RATINGS.POOR'),
  })[rating];

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => props.messages.length, scrollToBottom);
</script>

<template>
  <div
    ref="messageContainer"
    class="flex-1 overflow-y-auto mb-4 px-6 space-y-6"
  >
    <div
      v-for="(message, index) in messages"
      :key="index"
      class="flex"
      :class="getMessageAlignment(message.sender)"
    >
      <div
        class="flex items-end gap-1.5 max-w-[90%] md:max-w-[60%]"
        :class="getMessageDirection(message.sender)"
      >
        <Avatar
          :name="getAvatarName(message.sender)"
          rounded-full
          :size="24"
          class="shrink-0"
        />
        <div class="min-w-0">
          <div
            class="px-4 py-3 text-sm [overflow-wrap:break-word]"
            :class="getMessageStyle(message.sender)"
          >
            <div v-html="formatMessage(message.content)" />
          </div>
          <div
            v-if="!isUserMessage(message.sender) && message.sources?.length"
            class="mt-2 rounded-lg bg-n-alpha-1 px-3 py-2 outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <p class="mb-1 text-xs font-medium text-n-slate-11">
              {{ t('CAPTAIN.PLAYGROUND.SOURCES') }}
            </p>
            <a
              v-for="source in message.sources"
              :key="source.url"
              :href="source.url"
              target="_blank"
              rel="noopener noreferrer"
              class="block truncate text-xs text-n-blue-11 hover:underline"
            >
              {{ source.title || source.url }}
            </a>
          </div>
          <div
            v-if="!isUserMessage(message.sender) && message.testRunId"
            class="mt-2 flex items-center gap-1"
          >
            <span class="mr-1 text-xs text-n-slate-10">
              {{ t('CAPTAIN.PLAYGROUND.RATE') }}
            </span>
            <button
              v-for="rating in ['good', 'acceptable', 'poor']"
              :key="rating"
              type="button"
              class="rounded-md px-2 py-1 text-xs capitalize outline outline-1 -outline-offset-1"
              :class="
                message.rating === rating
                  ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
                  : 'bg-n-alpha-1 text-n-slate-10 outline-n-weak hover:bg-n-alpha-2'
              "
              @click="emit('rate', { message, rating })"
            >
              {{ ratingLabel(rating) }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="flex justify-start">
      <div class="flex items-start gap-1.5">
        <Avatar :name="getAvatarName('assistant')" rounded-full :size="24" />
        <div
          class="max-w-sm rounded-lg p-3 text-sm bg-n-solid-iris text-n-slate-12"
        >
          <div class="flex gap-1">
            <div class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce" />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.2s]"
            />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.4s]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
